import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useAuth } from '../../../contexts/AuthContext'
import EditClientModal from '../components/EditClientModal'
import ClientIdentitySidebar from '../components/client/ClientIdentitySidebar'
import OverviewTab from '../components/client/tabs/OverviewTab'
import AssessmentsTab from '../components/client/tabs/AssessmentsTab'
import ClientIntelligenceTab from '../components/client/tabs/ClientIntelligenceTab'
import SecurityPostureTab from '../components/client/tabs/SecurityPostureTab'
import ControlTestingTab from '../components/client/tabs/ControlTestingTab'
import RiskIntelligenceTab from '../components/client/tabs/RiskIntelligenceTab'
import CyberInsuranceReviewTab from '../../insurance-review/CyberInsuranceReviewTab'
import EnablementPlanTab from '../components/client/tabs/EnablementPlanTab'
import PoliciesTab from '../components/client/tabs/PoliciesTab'
import DocumentsTab from '../components/client/tabs/DocumentsTab'
import AdminTab from '../components/client/tabs/AdminTab'
import DiscoveryResultsView from '../components/client/views/DiscoveryResultsView'
import TechnologyStack from '../../assessment/pages/TechnologyStack'
import RiskRegisterTab from '../components/client/tabs/RiskRegisterTab'
import PlaceholderView from '../../../shared/components/PlaceholderView'
import LockedPagePlaceholder from '../../../components/common/LockedPagePlaceholder'
import ComingSoonPlaceholder from '../../../components/common/ComingSoonPlaceholder'
import ProgramOverview from '../../insights/pages/ProgramOverview'
import DomainAnalysis from '../../insights/pages/DomainAnalysis'
import RegulatoryLandscape from '../../insights/pages/RegulatoryLandscape'
import RiskThreats from '../../insights/pages/RiskThreats'
import FinancialResilience from '../../insights/pages/FinancialResilience'
import { getPageUnlockStatus, getPrerequisitePath } from '../../../shared/utils/pageUnlockStatus'

function ClientPortal() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const { clients, loading } = useAssessment()
  const { isAdmin } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showEditModal, setShowEditModal] = useState(false)
  const [activeKey, setActiveKey] = useState(searchParams.get('section') || 'overview')
  // Sync activeKey when URL search params change (e.g., from sidebar nav links)
  useEffect(() => {
    const section = searchParams.get('section')
    if (section && section !== activeKey) {
      setActiveKey(section)
    }
  }, [searchParams])

  // Scroll content area to top when active page changes
  useEffect(() => {
    const contentArea = document.querySelector('[data-content-area]') || document.querySelector('main')
    if (contentArea) contentArea.scrollTop = 0
    window.scrollTo(0, 0)
  }, [activeKey])

  const handleNavigate = (key) => {
    setActiveKey(key)
    setSearchParams({ section: key }, { replace: true })
  }

  const client = clients.find(c => c.id === clientId)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {isAdmin ? 'Client not found' : 'Access Error'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {isAdmin
            ? "The client you're looking for doesn't exist or has been deleted."
            : "You don't have access to this organization or it doesn't exist."
          }
        </p>
        {isAdmin && (
          <button onClick={() => navigate('/clients')} className="btn-primary">
            Back to Clients
          </button>
        )}
      </div>
    )
  }

  // Get latest assessment for unlock checks and Insights rendering
  const latestAssessment = (() => {
    const completed = (client.assessments || []).filter(a => a.status === 'completed').sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
    return completed[0] || (client.assessments || []).find(a => a.status === 'in_progress') || null
  })()

  const unlockStatus = getPageUnlockStatus(latestAssessment, client)

  // Helper to render locked page or content
  const renderWithUnlock = (pageId, sectionColor, prerequisiteLabel, content) => {
    const status = unlockStatus[pageId]
    if (!status?.unlocked) {
      return <LockedPagePlaceholder reason={status?.reason} prerequisitePath={getPrerequisitePath(status?.prerequisite, client.id)} prerequisiteLabel={prerequisiteLabel} sectionColor={sectionColor} />
    }
    return content
  }

  const hasInProgressAssessment = (client.assessments || []).some(a => a.status === 'in_progress')
  const assessmentLabel = hasInProgressAssessment ? 'Complete Active Assessment' : 'Start Assessment'

  const renderContent = () => {
    const completedAssessments = (client.assessments || []).filter(a => a.status === 'completed')
    const latestCompletedId = searchParams.get('assessmentId') || completedAssessments[0]?.id

    switch (activeKey) {
      case 'overview':
        return <OverviewTab client={client} />

      // ── DISCOVER: Client Intelligence ──
      case 'client-intelligence':
        return <ClientIntelligenceTab client={client} />

      // ── DISCOVER: Assessments (tabbed landing: Assessments + Follow-ups) ──
      case 'maturity-assessment':
        return <AssessmentsTab client={client} />

      // ── DISCOVER: Threat & Impact (direct section rendering) ──
      case 'risk-threat-context':
        return <RiskIntelligenceTab client={client} defaultSubTab="threat-impact" defaultSection="threat" />
      case 'risk-impact-calibration':
        return <RiskIntelligenceTab client={client} defaultSubTab="threat-impact" defaultSection="impact" />
      case 'cyber-insurance-review':
        return <CyberInsuranceReviewTab client={client} />

      // ── DISCOVER: Technology ──
      case 'tech-assets':
        return <SecurityPostureTab client={client} defaultView="assets" onNavigate={handleNavigate} />
      case 'tech-stack':
        return <TechnologyStack clientId={client.id} embedded={true} onNavigateBack={() => handleNavigate('overview')} />
      case 'control-testing':
        return <ControlTestingTab client={client} />

      // ── INSIGHTS (with unlock gates) ──
      case 'program-overview':
        return renderWithUnlock('program-overview', 'insights', assessmentLabel,
          latestCompletedId ? <ProgramOverview assessmentId={latestCompletedId} client={client} /> : <LockedPagePlaceholder reason="No completed assessment available" prerequisitePath={`/clients/${client.id}?section=maturity-assessment`} prerequisiteLabel={assessmentLabel} sectionColor="insights" />
        )
      case 'domain-analysis':
        return renderWithUnlock('domain-analysis', 'insights', assessmentLabel,
          latestCompletedId ? <DomainAnalysis assessmentId={latestCompletedId} client={client} /> : <LockedPagePlaceholder reason="No completed assessment available" prerequisitePath={`/clients/${client.id}?section=maturity-assessment`} prerequisiteLabel={assessmentLabel} sectionColor="insights" />
        )
      case 'risk-threats':
        return renderWithUnlock('risk-threats', 'insights', assessmentLabel,
          latestCompletedId ? <RiskThreats assessmentId={latestCompletedId} client={client} /> : <LockedPagePlaceholder reason="No completed assessment available" sectionColor="insights" />
        )
      case 'regulatory-landscape':
        return <RegulatoryLandscape assessmentId={latestCompletedId} client={client} />
      case 'compliance-insights':
        return renderWithUnlock('compliance-insights', 'insights', assessmentLabel,
          <RiskIntelligenceTab client={client} defaultSubTab="regulatory-compliance" defaultSection="compliance" />
        )
      case 'financial-resilience':
        return renderWithUnlock('financial-resilience', 'insights', assessmentLabel,
          latestCompletedId ? <FinancialResilience assessmentId={latestCompletedId} client={client} /> : <LockedPagePlaceholder reason="No completed assessment available" sectionColor="insights" />
        )
      case 'response-guidance':
        return <RiskIntelligenceTab client={client} defaultSubTab="regulatory-compliance" defaultSection="regulatory" breachResponseOnly />

      // ── GOVERN ──
      case 'enablement-plan':
        return renderWithUnlock('enablement-plan', 'govern', assessmentLabel,
          <EnablementPlanTab client={client} defaultSubTab="plan" singleView />
        )
      case 'frameworks':
        return <ComingSoonPlaceholder title="Frameworks" description="Select frameworks like NIST CSF 2.0, CIS Controls, HIPAA, PCI DSS, ISO 27001, SOC 2, NIST SP 800-53, and CMMC. The platform crosswalks each framework's controls onto your existing assessment so you can show coverage, identify gaps, and run a lightweight GRC engagement against any standard a client requires." />
      case 'advisory-reports':
      case 'executive-report':  // legacy alias
        return <ComingSoonPlaceholder title="Advisory Reports" description="A single hub for every client-facing export the platform produces: Executive Advisory Report, Cyber Insurance Policy Review, Risk Appetite Statement, Enablement Plan / POA&M, and more. Generate, customize, and download from one place instead of jumping between feature pages." />
      case 'risk-register':
        return <ComingSoonPlaceholder title="Risk Register" description="Centralized risk tracking with likelihood, impact, treatment plans, and ownership assignments tied to your assessment findings." />
      case 'third-party-risk':
        return <ComingSoonPlaceholder title="Third-Party Risk" description="Inventory critical vendors with criticality tiers, attestation status (SOC 2, ISO 27001), data sharing scope, contract dates, and last-review timestamps. Vendor risks roll up into the Risk Register so the advisor manages them in one place." />
      case 'audit-findings':
        return <ComingSoonPlaceholder title="Audit Findings" description="Track and manage audit findings, remediation status, and evidence collection across internal and external audits." />

      // ── DOCUMENT VAULT ──
      case 'policies':
        return <PoliciesTab client={client} />
      case 'documents':
        return <DocumentsTab client={client} />

      // ── MANAGE ──
      case 'admin-portal':
      case 'admin-users':
      case 'admin-activity':
        return isAdmin ? <AdminTab client={client} defaultSubTab={activeKey === 'admin-users' ? 'portal-users' : activeKey === 'admin-activity' ? 'activity-feed' : 'portal-settings'} /> : <OverviewTab client={client} />
      case 'integrations':
        return <ComingSoonPlaceholder title="Integrations" description="Connect with GRC platforms, SIEM tools, ticketing systems, and other security tools to streamline data collection and automate workflows." />

      // ── LEGACY routes — Results page removed; redirect to Program Overview ──
      case 'results-overview':
      case 'results-details':
      case 'results-threat':
      case 'risk-results-full':
        return renderWithUnlock('program-overview', 'insights', assessmentLabel,
          latestCompletedId
            ? <ProgramOverview assessmentId={latestCompletedId} client={client} />
            : <LockedPagePlaceholder reason="No completed assessment available" prerequisitePath={`/clients/${client.id}?section=maturity-assessment`} prerequisiteLabel={assessmentLabel} sectionColor="insights" />
        )

      // Legacy route alias kept (renders DiscoveryResultsView, not the deleted Results page)
      case 'risk-results':
        return <DiscoveryResultsView client={client} />
      case 'plan-overview':
        return renderWithUnlock('enablement-plan', 'govern', assessmentLabel, <EnablementPlanTab client={client} defaultSubTab="plan" singleView />)
      case 'reg-guidance':
        return <RegulatoryLandscape client={client} />
      case 'reg-compliance':
        return renderWithUnlock('compliance-insights', 'insights', assessmentLabel, <RiskIntelligenceTab client={client} defaultSubTab="regulatory-compliance" defaultSection="compliance" />)
      case 'reg-updates':
        return <RiskIntelligenceTab client={client} defaultSubTab="regulatory-compliance" defaultSection="updates" />

      default:
        return <OverviewTab client={client} />
    }
  }

  const isOverview = activeKey === 'overview'

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)' }}>
      {isOverview ? (
        /* Overview: full identity card on left, content on right */
        <div className="relative flex">
          <div className="flex-shrink-0 w-[272px]">
            <div className="w-[272px] py-4 px-3 sticky top-16 max-h-[calc(100vh-64px)] overflow-y-auto scrollbar-hide">
              <ClientIdentitySidebar
                client={client}
                onEdit={isAdmin ? () => setShowEditModal(true) : undefined}
              />
            </div>
          </div>
          <div className="flex-1 min-w-0 py-4 pr-6 pl-4">
            {renderContent()}
          </div>
        </div>
      ) : (
        /* All other pages: full-width content (client context is in the sidebar) */
        <div className="py-4 px-6">
          {renderContent()}
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && (
        <EditClientModal
          client={client}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  )
}

export default ClientPortal
